import type { Doc } from "building-types/doc";
import { BuiltinPlanViewType } from "building-types/views/BuiltinPlanViewType";
import type { PlanViewLevelMember } from "building-types/views/View3D";
import { mergeIn, setIn } from "common/utils/objectUtils";
import { nextSortIndexFromTable } from "common/utils/sortIndex";
import { snakeCaseToTitleCase } from "common/utils/strings";
import uid from "common/utils/uid";
import { planViewNew, view3DNew } from "./docViewTypes";
import { docHasPlanViewForLevel } from "./docViewUtils";
import { docEntityHandleHash } from "./entity/core/handleHash";

function addLevelPlanViews(doc: Doc, levelId: string): Doc {
  for (const referenceType of [BuiltinPlanViewType.FLOOR, BuiltinPlanViewType.ELECTRICAL]) {
    const id = uid();
    const planView = planViewNew(
      id,
      `${doc.levels[levelId].name} - ${snakeCaseToTitleCase(referenceType)}`,
      [{ entityId: levelId }],
      referenceType,
      nextSortIndexFromTable(doc.planViews),
    );
    doc = mergeIn(doc, ["planViews", id], planView);
  }

  return doc;
}

function docComponentHasExteriorView3D(doc: Doc, componentId: string): boolean {
  return Object.values(doc.view3Ds).some((view) => {
    if (view.levelId !== null) {
      return false;
    }
    const viewHash = docEntityHandleHash({ type: "VIEW_3D", id: view.id });
    return doc.componentMembers[viewHash] === componentId;
  });
}

function docAddComponentExteriorView3D(doc: Doc, componentId: string): Doc {
  const exteriorId = uid();
  const exteriorView3D = view3DNew(exteriorId, "3D View", null, nextSortIndexFromTable(doc.view3Ds));
  doc = mergeIn(doc, ["view3Ds", exteriorId], exteriorView3D);
  return setIn(doc, ["componentMembers", docEntityHandleHash({ type: "VIEW_3D", id: exteriorId })], componentId);
}

// Adds any missing component views.
export function docEnsureComponentDefaultViews(doc: Doc, componentId: string, levelIds: readonly string[]): Doc {
  // Add any missing exterior 3D view.
  if (!docComponentHasExteriorView3D(doc, componentId)) {
    doc = docAddComponentExteriorView3D(doc, componentId);
  }

  // Add any missing per-level floor plan views.
  for (const levelId of levelIds) {
    if (!docHasPlanViewForLevel(doc, levelId)) {
      doc = addLevelPlanViews(doc, levelId);
    }
  }

  return doc;
}

/**
 * Creates a complete set of default views for the given levels: per-level
 * Floor + Electrical plan views and a 3D view, plus one Foundation plan view
 * (on the lowest level) and one Roof plan view (spanning all levels).
 *
 * Used when bootstrapping a component's world so that it mirrors the view
 * structure the main world gets at doc init.
 *
 * When `componentId` is set, also creates a whole-component 3D view
 * (`levelId: null`) and registers it in `doc.componentMembers` so the
 * explorer can distinguish it from the main-world exterior view.
 */
export function docAddDefaultViewsForLevels(doc: Doc, levelIds: readonly string[], componentId?: string): Doc {
  if (levelIds.length === 0) {
    return doc;
  }

  const sortedLevelIds = [...levelIds].sort((a, b) => doc.levels[a].sortIndex - doc.levels[b].sortIndex);

  for (const levelId of sortedLevelIds) {
    doc = addLevelPlanViews(doc, levelId);

    const viewId = uid();
    const view3D = view3DNew(viewId, doc.levels[levelId].name, levelId, nextSortIndexFromTable(doc.view3Ds));
    doc = mergeIn(doc, ["view3Ds", viewId], view3D);
  }

  const foundationPlanViewId = uid();
  const foundationPlanView = planViewNew(
    foundationPlanViewId,
    "Foundation",
    [{ entityId: sortedLevelIds[0] }],
    BuiltinPlanViewType.FOUNDATION,
    nextSortIndexFromTable(doc.planViews),
  );
  doc = mergeIn(doc, ["planViews", foundationPlanViewId], foundationPlanView);

  const roofPlanViewId = uid();
  const roofPlanView = planViewNew(
    roofPlanViewId,
    "Roof",
    sortedLevelIds.map((id) => ({ entityId: id }) as PlanViewLevelMember),
    BuiltinPlanViewType.ROOF,
    nextSortIndexFromTable(doc.planViews),
  );
  doc = mergeIn(doc, ["planViews", roofPlanViewId], roofPlanView);

  if (componentId) {
    doc = docAddComponentExteriorView3D(doc, componentId);
  }

  return doc;
}
